import fs from 'node:fs'
import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import { DEPRECATED_ADMIN_PATH_ALIAS_SUFFIXES } from '@cli-constants/scaffolding.js'
import { toStrictJson } from '@cli-utils/files.js'
import { arraysEqual } from '@cli-utils/shared.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { createAdminPathAliases } from './create-admin-path-aliases.js'

export function scaffoldTsconfig(
  cwd: string,
  config: ResolvedBetterstartConfig
): { added: string[]; skipped: string[] } {
  const tsconfigPath = path.join(cwd, 'tsconfig.json')
  const added: string[] = []
  const skipped: string[] = []

  if (!fs.existsSync(tsconfigPath)) {
    skipped.push('tsconfig.json not found')
    return { added, skipped }
  }

  const raw = fs.readFileSync(tsconfigPath, 'utf-8')

  const stripped = toStrictJson(raw)

  let tsconfig: Record<string, unknown>
  try {
    tsconfig = JSON.parse(stripped)
  } catch {
    skipped.push('Failed to parse tsconfig.json')
    return { added, skipped }
  }

  const compilerOptions = (tsconfig.compilerOptions ?? {}) as Record<string, unknown>
  const paths = (compilerOptions.paths ?? {}) as Record<string, string[]>

  if (compilerOptions.resolveJsonModule === true) {
    skipped.push('compilerOptions.resolveJsonModule')
  } else {
    compilerOptions.resolveJsonModule = true
    added.push('compilerOptions.resolveJsonModule')
  }

  const namespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const aliases = createAdminPathAliases(config)
  for (const [alias, target] of Object.entries(aliases)) {
    if (arraysEqual(paths[alias], target)) {
      skipped.push(alias)
    } else {
      paths[alias] = target
      added.push(alias)
    }
  }

  for (const alias of Object.keys(paths)) {
    const isDefaultAdminAliasForCustomNamespace =
      namespace.alias !== '@admin' && (alias === '@admin' || alias.startsWith('@admin/'))
    const isDeprecatedCurrentAlias = DEPRECATED_ADMIN_PATH_ALIAS_SUFFIXES.some(
      (suffix) => alias === `${namespace.alias}${suffix}`
    )

    if (isDefaultAdminAliasForCustomNamespace || isDeprecatedCurrentAlias) {
      delete paths[alias]
      added.push(alias)
    }
  }

  compilerOptions.paths = paths
  tsconfig.compilerOptions = compilerOptions

  fs.writeFileSync(tsconfigPath, `${JSON.stringify(tsconfig, null, 2)}\n`, 'utf-8')

  return { added, skipped }
}
